import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, authAPI, sessionAPI, gameAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import MyProfile from './MyProfile';
import './GameSettings.css';

const DEFAULT_FONT_SIZE = 1.05;
const MIN_FONT_SIZE = 0.8;
const MAX_FONT_SIZE = 1.5;
const STEP = 0.05;

export default function GameSettings({ onClose, game, isHost, onGameUpdate }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Active tab state
  const [activeTab, setActiveTab] = useState('display');

  const [fontSize, setFontSize] = useState(() => {
    const saved = localStorage.getItem('gameFontSize');
    return saved ? parseFloat(saved) : DEFAULT_FONT_SIZE;
  });

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Profile modal state
  const [showProfile, setShowProfile] = useState(false);

  // Multiplayer state
  const [inviteCode, setInviteCode] = useState(game?.inviteCode || '');
  const [sessionOpen, setSessionOpen] = useState(game?.sessionOpen || false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [multiplayerError, setMultiplayerError] = useState('');

  // Game preferences state
  const [responseLength, setResponseLength] = useState(game?.settings?.responseLength || 'concise');
  const [nsfwContent, setNsfwContent] = useState(game?.settings?.nsfwContent || false);
  const [goreContent, setGoreContent] = useState(game?.settings?.goreContent ?? true);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [preferencesError, setPreferencesError] = useState('');
  const [preferencesSuccess, setPreferencesSuccess] = useState('');

  // Check admin status on mount
  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await adminAPI.getStatus();
      setIsAdmin(response.data.isAdmin);
      if (response.data.isAdmin) {
        loadAdminList();
      }
    } catch (err) {
      setIsAdmin(false);
    }
  };

  const loadAdminList = async () => {
    setLoadingAdmins(true);
    try {
      const response = await adminAPI.sendCommand('/listadmins', null);
      setAdminList(response.data.admins || []);
    } catch (err) {
      console.error('Failed to load admin list');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) return;

    setAdminError('');
    setAdminSuccess('');

    try {
      const response = await adminAPI.sendCommand(`/addadmin ${newAdminEmail.trim()}`, null);
      setAdminSuccess(response.data.message);
      setNewAdminEmail('');
      loadAdminList();
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Failed to add admin');
    }
  };

  const handleRemoveAdmin = async (email) => {
    setAdminError('');
    setAdminSuccess('');

    try {
      const response = await adminAPI.sendCommand(`/removeadmin ${email}`, null);
      setAdminSuccess(response.data.message);
      loadAdminList();
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Failed to remove admin');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);

    try {
      await authAPI.changePassword({
        currentPassword,
        newPassword
      });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--game-font-size', `${fontSize}rem`);
    localStorage.setItem('gameFontSize', fontSize.toString());
  }, [fontSize]);

  const handleFontSizeChange = (e) => {
    setFontSize(parseFloat(e.target.value));
  };

  const resetFontSize = () => {
    setFontSize(DEFAULT_FONT_SIZE);
  };

  const fontSizePercent = Math.round((fontSize / DEFAULT_FONT_SIZE) * 100);

  // Multiplayer functions
  const generateInviteCode = async () => {
    if (!game?._id) return;

    setGeneratingCode(true);
    setMultiplayerError('');
    setCodeCopied(false);

    try {
      const response = await sessionAPI.generateInviteCode(game._id);
      setInviteCode(response.data.inviteCode);
      if (onGameUpdate) {
        onGameUpdate({ inviteCode: response.data.inviteCode, inviteCodeExpiresAt: response.data.expiresAt });
      }
    } catch (err) {
      console.error('Generate invite code error:', err);
      setMultiplayerError(err.response?.data?.error || 'Failed to generate invite code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyInviteCode = async () => {
    if (!inviteCode) return;

    try {
      await navigator.clipboard.writeText(inviteCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = inviteCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const toggleSessionOpen = async () => {
    if (!game?._id) return;

    setMultiplayerError('');

    try {
      const response = await sessionAPI.toggleSessionOpen(game._id);
      setSessionOpen(response.data.sessionOpen);
      if (onGameUpdate) {
        onGameUpdate({ sessionOpen: response.data.sessionOpen });
      }
    } catch (err) {
      console.error('Toggle session error:', err);
      setMultiplayerError(err.response?.data?.error || 'Failed to update session status');
    }
  };

  const revokeInviteCode = async () => {
    if (!game?._id) return;

    setMultiplayerError('');

    try {
      await sessionAPI.revokeInviteCode(game._id);
      setInviteCode('');
      if (onGameUpdate) {
        onGameUpdate({ inviteCode: null, inviteCodeExpiresAt: null });
      }
    } catch (err) {
      console.error('Revoke invite code error:', err);
      setMultiplayerError(err.response?.data?.error || 'Failed to revoke invite code');
    }
  };

  // Game preferences handlers
  const updatePreference = async (key, value) => {
    if (!game?._id) return;

    setSavingPreferences(true);
    setPreferencesError('');
    setPreferencesSuccess('');

    try {
      const response = await gameAPI.updateSettings(game._id, { [key]: value });

      if (key === 'responseLength') setResponseLength(value);
      if (key === 'nsfwContent') setNsfwContent(value);
      if (key === 'goreContent') setGoreContent(value);

      if (onGameUpdate) {
        onGameUpdate({ settings: response.data.settings });
      }

      setPreferencesSuccess('Saved');
      setTimeout(() => setPreferencesSuccess(''), 2000);
    } catch (err) {
      console.error('Update preference error:', err);
      setPreferencesError(err.response?.data?.error || 'Failed to update setting');
    } finally {
      setSavingPreferences(false);
    }
  };

  // Build tabs array based on context
  const tabs = [
    { id: 'display', label: 'Display', icon: '🎨' },
    ...(game ? [{ id: 'game', label: 'Game', icon: '⚔️' }] : []),
    { id: 'account', label: 'Account', icon: '👤' },
    ...(game && isHost ? [{ id: 'multiplayer', label: 'Party', icon: '👥' }] : []),
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: '🛡️' }] : [])
  ];

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel-v2" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-header-v2">
          <h3>Settings</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="settings-tab-content">
          {/* Display Tab */}
          {activeTab === 'display' && (
            <div className="tab-panel">
              <div className="panel-section">
                <div className="section-header-inline">
                  <h4>Chat Font Size</h4>
                  <span className="value-badge">{fontSizePercent}%</span>
                </div>
                <div className="slider-row">
                  <span className="slider-label-sm">A</span>
                  <input
                    type="range"
                    min={MIN_FONT_SIZE}
                    max={MAX_FONT_SIZE}
                    step={STEP}
                    value={fontSize}
                    onChange={handleFontSizeChange}
                    className="settings-slider"
                  />
                  <span className="slider-label-lg">A</span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={resetFontSize}>
                  Reset to Default
                </button>
              </div>

              <div className="panel-section">
                <h4>Preview</h4>
                <div className="font-preview-box" style={{ fontSize: `${fontSize}rem` }}>
                  The tavern door creaks open. A grizzled dwarf eyes you suspiciously.
                  "What'll it be, stranger?"
                </div>
              </div>
            </div>
          )}

          {/* Game Tab */}
          {activeTab === 'game' && game && (
            <div className="tab-panel">
              <div className="panel-section">
                <h4>DM Response Length</h4>
                <p className="section-hint">How detailed should the DM's descriptions be?</p>

                {preferencesError && <div className="inline-error">{preferencesError}</div>}
                {preferencesSuccess && <div className="inline-success">{preferencesSuccess}</div>}

                <div className="option-cards">
                  <button
                    className={`option-card ${responseLength === 'concise' ? 'active' : ''}`}
                    onClick={() => updatePreference('responseLength', 'concise')}
                    disabled={savingPreferences}
                  >
                    <span className="option-card-title">Concise</span>
                    <span className="option-card-desc">Quick & punchy</span>
                  </button>
                  <button
                    className={`option-card ${responseLength === 'standard' ? 'active' : ''}`}
                    onClick={() => updatePreference('responseLength', 'standard')}
                    disabled={savingPreferences}
                  >
                    <span className="option-card-title">Standard</span>
                    <span className="option-card-desc">Balanced</span>
                  </button>
                  <button
                    className={`option-card ${responseLength === 'detailed' ? 'active' : ''}`}
                    onClick={() => updatePreference('responseLength', 'detailed')}
                    disabled={savingPreferences}
                  >
                    <span className="option-card-title">Detailed</span>
                    <span className="option-card-desc">Rich & immersive</span>
                  </button>
                </div>
              </div>

              <div className="panel-section">
                <h4>Content Filters</h4>

                <div className="toggle-row">
                  <div className="toggle-text">
                    <span className="toggle-title">Mature Content</span>
                    <span className="toggle-desc">Adult language, suggestive themes</span>
                  </div>
                  <label className="toggle-switch-v2">
                    <input
                      type="checkbox"
                      checked={nsfwContent}
                      onChange={(e) => updatePreference('nsfwContent', e.target.checked)}
                      disabled={savingPreferences}
                    />
                    <span className="toggle-track"></span>
                  </label>
                </div>

                <div className="toggle-row">
                  <div className="toggle-text">
                    <span className="toggle-title">Graphic Violence</span>
                    <span className="toggle-desc">Detailed blood & injuries</span>
                  </div>
                  <label className="toggle-switch-v2">
                    <input
                      type="checkbox"
                      checked={goreContent}
                      onChange={(e) => updatePreference('goreContent', e.target.checked)}
                      disabled={savingPreferences}
                    />
                    <span className="toggle-track"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="tab-panel">
              <div className="panel-section">
                <div className="account-card">
                  <div className="account-avatar">
                    {user?.username?.charAt(0).toUpperCase()}
                  </div>
                  <div className="account-details">
                    <span className="account-name">{user?.username}</span>
                    <span className="account-email">{user?.email}</span>
                  </div>
                </div>

                <div className="account-actions">
                  <button className="btn btn-primary" onClick={() => setShowProfile(true)}>
                    View Profile
                  </button>
                </div>
              </div>

              <div className="panel-section">
                <h4>Security</h4>

                {passwordError && <div className="inline-error">{passwordError}</div>}
                {passwordSuccess && <div className="inline-success">{passwordSuccess}</div>}

                {showPasswordForm ? (
                  <form onSubmit={handlePasswordChange} className="password-form-v2">
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current password"
                      required
                    />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      required
                      minLength={6}
                    />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordError('');
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={changingPassword}>
                        {changingPassword ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowPasswordForm(true)}
                  >
                    Change Password
                  </button>
                )}
              </div>

              <div className="panel-section">
                <button className="btn btn-danger full-width" onClick={handleLogout}>
                  Log Out
                </button>
              </div>
            </div>
          )}

          {/* Multiplayer Tab */}
          {activeTab === 'multiplayer' && game && isHost && (
            <div className="tab-panel">
              <div className="panel-section">
                <div className="section-header-inline">
                  <h4>Invite Players</h4>
                  <span className="badge-host">HOST</span>
                </div>
                <p className="section-hint">Share this code with friends. Expires in 2 hours.</p>

                {multiplayerError && <div className="inline-error">{multiplayerError}</div>}

                {inviteCode ? (
                  <div className="invite-display">
                    <div className="invite-code-box">
                      <span className="invite-code-text">{inviteCode}</span>
                      <button
                        className={`copy-btn-v2 ${codeCopied ? 'copied' : ''}`}
                        onClick={copyInviteCode}
                      >
                        {codeCopied ? '✓' : '📋'}
                      </button>
                    </div>
                    <div className="invite-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={generateInviteCode}
                        disabled={generatingCode}
                      >
                        New Code
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={revokeInviteCode}>
                        Revoke
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary full-width"
                    onClick={generateInviteCode}
                    disabled={generatingCode}
                  >
                    {generatingCode ? 'Generating...' : 'Generate Invite Code'}
                  </button>
                )}
              </div>

              <div className="panel-section">
                <h4>Session Settings</h4>

                <div className="toggle-row">
                  <div className="toggle-text">
                    <span className="toggle-title">Open Session</span>
                    <span className="toggle-desc">Allow anyone with code to join</span>
                  </div>
                  <label className="toggle-switch-v2">
                    <input
                      type="checkbox"
                      checked={sessionOpen}
                      onChange={toggleSessionOpen}
                    />
                    <span className="toggle-track"></span>
                  </label>
                </div>

                {game.participants && game.participants.length > 0 && (
                  <div className="party-count">
                    <span className="party-icon">👥</span>
                    <span>{game.participants.length} player{game.participants.length !== 1 ? 's' : ''} in party</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Admin Tab */}
          {activeTab === 'admin' && isAdmin && (
            <div className="tab-panel">
              <div className="panel-section">
                <div className="section-header-inline">
                  <h4>Admin Commands</h4>
                  <span className="badge-admin">ADMIN</span>
                </div>
                <div className="command-grid">
                  <code>/healme</code>
                  <code>/heal &lt;name&gt;</code>
                  <code>/spill me</code>
                  <code>/giveme "item"</code>
                  <code>/help</code>
                </div>
              </div>

              <div className="panel-section">
                <h4>Admin Whitelist</h4>

                {adminError && <div className="inline-error">{adminError}</div>}
                {adminSuccess && <div className="inline-success">{adminSuccess}</div>}

                <form onSubmit={handleAddAdmin} className="add-admin-row">
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                  <button type="submit" className="btn btn-primary btn-sm">Add</button>
                </form>

                <div className="admin-list-v2">
                  {loadingAdmins ? (
                    <p className="loading-text">Loading...</p>
                  ) : (
                    adminList.map((admin) => (
                      <div key={admin.email} className="admin-row">
                        <div className="admin-info-v2">
                          <span className="admin-name">{admin.username}</span>
                          <span className="admin-email-v2">{admin.email}</span>
                        </div>
                        {admin.email !== 'cole@neonforge.co' && (
                          <button
                            onClick={() => handleRemoveAdmin(admin.email)}
                            className="btn btn-danger btn-sm"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="settings-footer-v2">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>

      {/* My Profile Modal */}
      {showProfile && <MyProfile onClose={() => setShowProfile(false)} />}
    </div>
  );
}
